import { forwardRef, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { propertyCardHomeDetailsStyles } from './styles/property-card-home-details.styles';
import type { PropertyCardHomeDetailsPropsInterface } from './types/property-card-home-details.types';

const DELIMITER_MAP = {
  pipe: <span className="c11n-home-details__pipe" />,
  comma: ', ',
} as const;

/**
 * Displays the home details inside of a `PropertyCard`.
 *
 * @since 8.67.0
 */
export const PropertyCardHomeDetails = forwardRef<
  HTMLUListElement,
  PropertyCardHomeDetailsPropsInterface
>((props: PropertyCardHomeDetailsPropsInterface, forwardedRef) => {
  const { className, css: cssProp, data, ...rest } = props;

  const styles = css(propertyCardHomeDetailsStyles, cssProp);

  const content = useMemo(
    () =>
      data.map(
        (
          {
            label,
            value,
            delimiter = 'pipe',
          }: PropertyCardHomeDetailsPropsInterface['data'][number],
          index: number,
        ) => {
          const delimiterEl =
            index === data.length - 1
              ? null
              : (DELIMITER_MAP[delimiter as keyof typeof DELIMITER_MAP] ?? '|');

          return (
            <li key={index}>
              {value ? <b>{value}</b> : null} {label ?? null}
              {delimiterEl}
            </li>
          );
        },
      ),
    [data],
  );

  return (
    <ul
      ref={forwardedRef}
      data-c11n-component="PropertyCard.HomeDetails"
      data-c11n-version={__C11N_VERSION__}
      className={cx('c11n', 'c11n_property-card__home-details', styles, className)}
      {...rest}
    >
      {content}
    </ul>
  );
});

PropertyCardHomeDetails.displayName = 'PropertyCard.HomeDetails';
